import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { PortfolioProperty } from "../types/ownerPortfolio.types";
import { getPortfolioPropertyText } from "../utils/getPortfolioPropertyText";

interface PortfolioSidebarProps {
  property: PortfolioProperty;
  title: string;
  closeLabel: string;
  testId: string;
  titleId: string;
  onClose: () => void;
  children: React.ReactNode;
}

export default function PortfolioSidebar({
  property,
  title,
  closeLabel,
  testId,
  titleId,
  onClose,
  children,
}: PortfolioSidebarProps) {
  const text = getPortfolioPropertyText(property);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    document.addEventListener("keydown", handleEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-[#0d1117]/70"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid={testId}
        className="relative flex h-full w-full max-w-md flex-col border-l border-white/[0.08] bg-[#161b22] shadow-[-16px_0_40px_rgba(0,0,0,0.45)]"
      >
        <div className="flex items-start justify-between gap-3 border-b border-white/[0.08] px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="m-0 text-base font-semibold text-[#f0f6fc]">
              {title}
            </h2>
            <p className="m-0 mt-1 text-sm text-[#f0f6fc]">{text.place.headline}</p>
            {text.place.condominium && (
              <p className="m-0 mt-1 text-sm text-[#8b949e]">{text.place.condominium}</p>
            )}
            <p className="m-0 mt-1 text-xs text-[#8b949e]">
              {text.unit !== "—" ? `Unidade ${text.unit} · ` : ""}
              Contrato {text.contract}
            </p>
            {text.place.address && <p className="m-0 mt-1 text-xs text-[#8b949e]">{text.place.address}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-[#8b949e] transition-colors hover:bg-white/[0.06] hover:text-[#f0f6fc]"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{children}</div>
      </aside>
    </div>,
    document.body
  );
}
